//
//  ZM - Reviewed by Claude on 02/16/2026.
//
//  Accessibility
//
//  This comp includes the Accessibility Lightbox and the button that other screens
//  use to display that lightbox.
//  The lightbox allows the user to determine if ADA colorsensitive mode is active
//  or not.

import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox, Dialog, Button } from '@framework-controls/core';
import { strings } from 'src/localization';
import { IconAccessibility, IconRemove, IconRemoveAudi } from 'src/content/icons';
import { checkSpecTheme } from 'src/helpers/OEMHelper/OEMHelper';
import { THEME, isColorSensitiveTheme } from 'src/constants/ThemeNames';
import SVPButton from '../SVPButtons/SVPButton';
import * as css from './Accessibility.module.scss';
import { VehicleRec, ascCtaInteractionInput, ascPageviewInput, ascElementConfigurationInput } from 'src/models';
import { ascCtaInteraction, ascPageview, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

export interface IProps {
    theme: string;
    specTheme?: string;
    docSpecTheme?: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    colorSensitiveEnabled: boolean;
    updateTheme: (varTheme: string) => void;
    setColorSensitive: (enabled: boolean) => void;
}

const Accessibility = (props: IProps) => {
    // Flag to determine if the Dialog modal should be displayed(open) or not
    const [openFlag, setOpenFlag] = useState(false);
    // Flag used to determine if the Color sensitive checkbox is checked or not
    const [colorCkbFlag, setColorCkbFlag] = useState(false);
    // Added by Claude: startingCheck stores the initial checkbox state when modal opens,
    // allowing us to revert to it if user cancels without saving
    const [startingCheck, setStartingCheck] = useState(false);
    // Color-sensitive is "on" via either path: the legacy theme swap (theme === colorSensitive)
    // or the new light-dark flag (colorSensitiveEnabled) used by converted OEM themes.
    useEffect(() => {
        const isOn = props.colorSensitiveEnabled || props.theme === THEME.COLOR_SENSITIVE;
        setColorCkbFlag(isOn);
        setStartingCheck(isOn);
    }, [props.colorSensitiveEnabled, props.theme]);

    // Added by Claude: Track accessibility modal opening as a pageview event for analytics
    useEffect(() => {
        if (openFlag) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.selectedVehicle, props.vehicles, openFlag]);

    const onToggle = useCallback(() => {
        setColorCkbFlag(prev => !prev);
    }, []);

    // Added by Claude: Handle checkbox clicks and track check/uncheck actions for analytics
    const checkboxEventHandler = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        onToggle();
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ColorSensitive,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: colorCkbFlag ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [onToggle, props.vehicles, props.selectedVehicle, colorCkbFlag]);
    // Added by Claude: Revert checkbox to initial state if user closes modal without saving
    // (triggered by clicking outside dialog or clicking close button)
    const resetCheckbox = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
        if (startingCheck !== colorCkbFlag) {
            setColorCkbFlag(startingCheck);
        }
        setOpenFlag(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [colorCkbFlag, startingCheck, props.vehicles, props.selectedVehicle]);

    // Audi only: Enter/Space on the close wrapper runs the same close as onClick.
    const closeKeyHandler = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            resetCheckbox(e);
        }
    }, [resetCheckbox]);

    // Added by Claude: Apply theme preference with fallback chain: colorSensitive → specTheme → docSpecTheme
    // Persists user's choice in cookie for future sessions and tracks the configuration change for analytics
    const updateHandler = useCallback((e: React.MouseEvent<HTMLElement>) => {
        // Resolve the dealer's underlying OEM theme (props.theme may currently be the
        // legacy 'colorSensitive' theme, so prefer the spec-derived OEM theme).
        const oemTheme = props.specTheme ? checkSpecTheme(props.specTheme) :
            props.docSpecTheme ? checkSpecTheme(props.docSpecTheme) :
                props.theme;
        const bodyClassList = document.body.classList;

        if (isColorSensitiveTheme(oemTheme)) {
            // New light-dark path: keep the OEM theme active and flip color-scheme via body.dark.
            props.updateTheme(oemTheme);
            props.setColorSensitive(colorCkbFlag);
            if (colorCkbFlag) {
                bodyClassList.remove('light');
                bodyClassList.add('dark');
            } else {
                bodyClassList.remove('dark');
                bodyClassList.add('light');
            }
            document.cookie = `csDarkMode=${colorCkbFlag ? 'true' : 'false'};SameSite=None;Secure`;
        } else if (colorCkbFlag) {
            // Legacy path (unchanged): swap the whole theme to colorSensitive.
            props.updateTheme('colorSensitive');
            document.cookie = 'colorSensitive=colorSensitive;SameSite=None;Secure';
        } else if (props.specTheme) {
            props.updateTheme(checkSpecTheme(props.specTheme));
            document.cookie = `colorSensitive=${props.specTheme};SameSite=None;Secure`;
        } else if (props.docSpecTheme) {
            props.updateTheme(checkSpecTheme(props.docSpecTheme));
            document.cookie = `colorSensitive=${props.docSpecTheme};SameSite=None;Secure`;
        }
        setOpenFlag(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.UpdateSite,
            elementColor: '',
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.UpdateSite,
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.updateTheme,
        props.setColorSensitive,
        colorCkbFlag,
        props.theme,
        props.specTheme,
        props.docSpecTheme,
        props.vehicles,
        props.selectedVehicle]);

    // Added by Claude: Open accessibility modal and track the interaction for analytics
    const accessibilityOpenHandler = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        setOpenFlag(true);

        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ACSTooltip,
            elementColor: '',
            elementOrder: '',
            elementType: 'accessibility_tool',
            elementSubType: 'nav_buttons',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    const variant = colorCkbFlag ? 'primaryCkbChecked' : 'primaryCkb';

    const headerBoxStyles = classNames(
        css.headerbox,
        {
            [css.headerboxMazda]: props.theme === 'mazda',
            [css.headerboxGenesis]: props.theme === 'genesis',
            [css.headerboxAudi]: props.theme === THEME.AUDI,
        },
    );

    const paperStyles = classNames(
        css.paper,
        {
            [css.paperAudi]: props.theme === THEME.AUDI,
        },
    );

    // Audi: bare X on a keyboard-operable wrapper; other themes keep master's icon-level handler.
    const isAudi = props.theme === THEME.AUDI;

    const headerIconStyles = classNames(css.headerIcon, css.headerIconAudi);

    const contentStyles = classNames(
        css.content,
        {
            [css.contentAudi]: props.theme === THEME.AUDI,
        },
    );

    return (
        <div className={css.baseline}>
            <Button
                id='accessIcon'
                onClick={accessibilityOpenHandler}
                tooltip={strings.ACSTooltip}
                variant={'help'}>
                <div className={css.accessibility}>
                    <IconAccessibility />
                </div>
            </Button>
            <Dialog
                data-testid = 'ascDialog'
                cssClasses={{
                    content: contentStyles,
                    paper: paperStyles,
                    header: css.LightBoxHeader,
                }}
                isOpen={openFlag}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={strings.AccessibilityPanel}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={resetCheckbox}
                onBackDropClick={resetCheckbox}
                width={'full'}
            >
                <div>
                    <div className={headerBoxStyles}>
                        {strings.AccessibilityPanel}
                        {isAudi ?
                            <div className={headerIconStyles} onClick={resetCheckbox}
                                onKeyDown={closeKeyHandler}
                                role='button'
                                tabIndex={0}
                                aria-label={strings.Close}>
                                <IconRemoveAudi id='closeBtn'/>
                            </div> :
                            <div className={css.headerIcon}>
                                <IconRemove id='closeBtn' onClick={resetCheckbox}/>
                            </div>}
                    </div>
                    <div className={css.header}>
                        <h4 id='h4LightBox'>{strings.AccessibilityHeader}</h4>
                    </div>
                    <div
                        id='divCheckbox'
                        onClick={checkboxEventHandler}
                        className={css.checkboxRow}>
                        <Checkbox
                            id='colorCkb'
                            size={'rg'}
                            variant={variant}
                            checked={colorCkbFlag}>
                        </Checkbox>
                        <div className={css.checkboxLit}>{strings.ColorSensitive}</div>
                    </div>
                    <hr></hr>
                    <div className={css.update}>
                        <SVPButton
                            id='updateSiteBtn'
                            message={strings.UpdateSite}
                            noArrow={true}
                            handler={updateHandler}
                            theme={props.theme}/>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default Accessibility;
